import { monthNames, tr } from '@/lib/locale';

/*
 * Relative and exact time labels, computed in the browser so they stay honest
 * as a tab sits open between refetches. Shared by the activity feed (which
 * blocks entries by day) and by every "2 days ago" elsewhere on the site.
 *
 * Month names come from the locale bundles rather than `toLocaleDateString`,
 * whose short month shifts with the runtime's locale data ("Sep" vs "Sept") -
 * these should read the same for everyone on a given language.
 */

const MINUTE = 60_000;

export const DAY = 86_400_000;

/** Short month names for the active language. */
export function months(): readonly string[] {
  return monthNames();
}

export function startOfDay(at: Date): number {
  return new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
}

export function minutesSince(iso: string | null, now: Date): number | null {
  if (!iso) return null;

  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;

  return Math.max(0, Math.floor((now.getTime() - at) / MINUTE));
}

/** "12 Sep 2026". */
export function dayLabel(date: Date): string {
  return `${date.getDate()} ${months()[date.getMonth()]} ${date.getFullYear()}`;
}

function clock(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** "just now" · "6m ago" · "3h ago" · "2d ago"; empty for an unknown time. */
export function agoLabel(iso: string | null, now: Date = new Date()): string {
  const minutes = minutesSince(iso, now);
  if (minutes === null) return '';

  if (minutes < 1) return tr('common:time.justNow');
  if (minutes < 60) return tr('common:time.minutesAgoShort', { count: minutes });

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return tr('common:time.hoursAgoShort', { count: hours });

  return tr('common:time.daysAgoShort', { count: Math.floor(hours / 24) });
}

/**
 * The exact moment behind a relative label, for its tooltip: "Today at 13:45",
 * "Yesterday at 23:24", "12 Sep, 2015 at 14:13". Empty when the time is
 * unknown, which is what `Hint` treats as "no tooltip".
 */
export function exactTime(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const day = startOfDay(date);
  const today = startOfDay(now);

  if (day === today) return tr('common:time.todayAt', { time: clock(date) });
  if (day === today - DAY) return tr('common:time.yesterdayAt', { time: clock(date) });

  return tr('common:time.dateAt', {
    date: `${date.getDate()} ${months()[date.getMonth()]}, ${date.getFullYear()}`,
    time: clock(date),
  });
}
